import React from "react";
import { InlineMath } from "react-katex";

import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import IntervalInput from "../../../../components/inputs/IntervalInput";
import {
  pickCurveStyle,
  sampleAnchorCurve,
} from "./helpers/smoothGraph";

import {
  randInt,
  parseSingleInterval,
  parseIntervalsUnion,
  getIntervalRaw,
  intervalsEqualList,
} from "../../temat6_zadania_uzupelniajace_maturalne/podtemat3_otwarte_types/helpers/MaturaPiecewiseFunctionUtils";

/* =============================================================
   TASK: ReadDomainFromGraphTask
   Cel: Odczytaj dziedzinę funkcji z wykresu.

   Warianty:
   (A) ciągła łamana (2 odcinki) -> dziedzina = jeden przedział
   (B) rozłączna (2 odcinki z przerwą) -> dziedzina = suma 2 przedziałów

   Uwaga:
   - allowUnion = true, bo czasem dziedzina to suma
   - checkAnswer porównuje 1 lub 2 przedziały zależnie od oczekiwań
============================================================= */

function pickClosedFlag() {
  return randInt(0, 1) === 1;
}

function pickY() {
  return randInt(-2, 5);
}

function lerp(xa, ya, xb, yb, x) {
  return ya + ((yb - ya) * (x - xa)) / (xb - xa);
}

/* -------------------- WARIANT A: CIĄGŁA ŁAMANA -------------------- */

function buildDataContinuous() {
  // Dziedzina: [x1, x3] z jednym załamaniem w x2
  // Chcemy różnorodność: start też może być -1/0/1
  const leftOptions = [-5, -4, -3, -2, -1, 0, 1];

  for (let attempt = 0; attempt < 500; attempt++) {
    const x1 = leftOptions[randInt(0, leftOptions.length - 1)];
    const x3Min = x1 + 4; // min długość 4
    if (x3Min > 5) continue;

    const x3 = randInt(x3Min, 5);
    const x2 = randInt(x1 + 1, x3 - 1);

    // końce domknięte/otwarte
    const leftClosed = pickClosedFlag();
    const rightClosed = pickClosedFlag();

    // dobieramy wysokości w węzłach
    const y1 = pickY();
    const y2 = pickY();
    const y3 = pickY();

    // żeby faktycznie było "trochę rośnie potem maleje" lub odwrotnie,
    // unikamy sytuacji prawie prostej
    if (y1 === y2 && y2 === y3) continue;

    // pilnujemy, żeby nie było ultrastromo
    const s1 = (y2 - y1) / (x2 - x1);
    const s2 = (y3 - y2) / (x3 - x2);
    if (Math.abs(s1) > 4 || Math.abs(s2) > 4) continue;

    const eps = 0;

    const points = {
      A: {
        x: x1,
        y: y1,
        markerSymbol: leftClosed ? "circle-filled" : "circle-open",
        showLabel: false,
      },
      Aline: {
        x: x1 + eps,
        y: lerp(x1, y1, x2, y2, x1 + eps),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },

      B: {
        x: x2,
        y: y2,
        showDot: false,
        showLabel: false,
      },
      Bline: {
        x: x2 + eps,
        y: lerp(x2, y2, x3, y3, x2 + eps),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },

      Cline: {
        x: x3 - eps,
        y: lerp(x2, y2, x3, y3, x3 - eps),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      C: {
        x: x3,
        y: y3,
        markerSymbol: rightClosed ? "circle-filled" : "circle-open",
        showLabel: false,
      },
    };

    const domainIntervals = [
      { left: x1, right: x3, leftClosed, rightClosed },
    ];

    return {
      variant: "continuous",
      points,
      curves: [
        sampleAnchorCurve(
          [
            { x: x1, y: y1 },
            { x: x2, y: y2 },
            { x: x3, y: y3 },
          ],
          {
            style: pickCurveStyle(),
            openStart: !leftClosed,
            openEnd: !rightClosed,
          }
        ),
      ],
      domainIntervals,
      xRange: [-6, 6],
      yRange: [-6, 6],
    };
  }

  return buildDataContinuous();
}

/* -------------------- WARIANT B: ROZŁĄCZNA (SUMA PRZEDZIAŁÓW) -------------------- */

function buildDataDisconnected() {
  // Dwie części wykresu: lewa na [x1,x2], prawa na [x3,x4], z przerwą (x2 < x3)
  const leftStartOptions = [-5, -4, -3, -2, -1, 0, 1];

  for (let attempt = 0; attempt < 800; attempt++) {
    const x1 = leftStartOptions[randInt(0, leftStartOptions.length - 1)];

    // lewy odcinek ma mieć sensowną długość (>=2)
    const x2Min = x1 + 2;
    if (x2Min > 3) continue;
    const x2 = randInt(x2Min, 3);

    // przerwa co najmniej 1
    const x3Min = x2 + 1;
    if (x3Min > 4) continue;
    const x3 = randInt(x3Min, 4);

    // prawy odcinek długość >=2
    const x4Min = x3 + 2;
    if (x4Min > 5) continue;
    const x4 = randInt(x4Min, 5);

    // losujemy domknięcia dla czterech końców
    const L1 = pickClosedFlag();
    const R1 = pickClosedFlag();
    const L2 = pickClosedFlag();
    const R2 = pickClosedFlag();

    // wysokości (żeby było w oknie i wyglądało "maturalnie")
    const y1 = pickY();
    const y2 = pickY();
    const y3 = pickY();
    const y4 = pickY();

    // kontrola stromizn
    const sL = (y2 - y1) / (x2 - x1);
    const sR = (y4 - y3) / (x4 - x3);
    if (Math.abs(sL) > 4 || Math.abs(sR) > 4) continue;

    const eps = 0;

    const points = {
      // LEWA CZĘŚĆ
      A: {
        x: x1,
        y: y1,
        markerSymbol: L1 ? "circle-filled" : "circle-open",
        showLabel: false,
      },
      Aline: {
        x: x1 + eps,
        y: lerp(x1, y1, x2, y2, x1 + eps),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      Bline: {
        x: x2 - eps,
        y: lerp(x1, y1, x2, y2, x2 - eps),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      B: {
        x: x2,
        y: y2,
        markerSymbol: R1 ? "circle-filled" : "circle-open",
        showLabel: false,
      },

      // PRAWA CZĘŚĆ
      C: {
        x: x3,
        y: y3,
        markerSymbol: L2 ? "circle-filled" : "circle-open",
        showLabel: false,
      },
      Cline: {
        x: x3 + eps,
        y: lerp(x3, y3, x4, y4, x3 + eps),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      Dline: {
        x: x4 - eps,
        y: lerp(x3, y3, x4, y4, x4 - eps),
        showDot: false,
        showMarker: false,
        showLabel: false,
        label: "",
      },
      D: {
        x: x4,
        y: y4,
        markerSymbol: R2 ? "circle-filled" : "circle-open",
        showLabel: false,
      },
    };

    const domainIntervals = [
      { left: x1, right: x2, leftClosed: L1, rightClosed: R1 },
      { left: x3, right: x4, leftClosed: L2, rightClosed: R2 },
    ];

    return {
      variant: "disconnected",
      points,
      curves: [
        sampleAnchorCurve(
          [
            { x: x1, y: y1 },
            { x: x2, y: y2 },
          ],
          {
            style: pickCurveStyle(),
            openStart: !L1,
            openEnd: !R1,
          }
        ),
        sampleAnchorCurve(
          [
            { x: x3, y: y3 },
            { x: x4, y: y4 },
          ],
          {
            style: pickCurveStyle(),
            openStart: !L2,
            openEnd: !R2,
          }
        ),
      ],
      domainIntervals,
      xRange: [-6, 6],
      yRange: [-6, 6],
    };
  }

  return buildDataDisconnected();
}

/* -------------------- LOSOWANIE WARIANTU -------------------- */

function buildData() {
  // 50/50: ciągła vs rozłączna
  return Math.random() < 0.5 ? buildDataContinuous() : buildDataDisconnected();
}

/* -------------------- API TASKA -------------------- */

export function generateTask() {
  return {
    type: "ReadDomainFromGraphTask",
    data: buildData(),
  };
}

export function renderTask({ data }) {
  const sizePx = 330; // ~30% mniej (jak chciałeś)

  return (
    <div className="flex flex-col gap-3">
      <p>Na wykresie przedstawiono funkcję. Odczytaj jej dziedzinę.</p>

      <div style={{ margin: "0 auto", maxWidth: sizePx }}>
        <GraphViewer
          width={sizePx}
          points={data.points}
          curves={data.curves}
          xRange={data.xRange}
          yRange={data.yRange}
        />
      </div>

      <div>
        Dziedzinę funkcji zapisz w postaci{" "}
        <InlineMath math={"x\\in\\_\\_\\_\\_"} />.
      </div>
    </div>
  );
}

export function renderInput(_task, userInput = {}, onChange, disabled) {
  const ui = userInput || {};
  const value = ui.D ?? "";

  const handleChange = (val) => {
    onChange({
      ...(userInput || {}),
      D: val,
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <div>
        <strong>Dziedzina:</strong>
      </div>
      <div><InlineMath math={"x\\in"} /></div>
      <div>
        <IntervalInput
          value={value}
          onChange={handleChange}
          allowUnion={true}
          disabled={disabled}
          previewWithX={true}
        />
      </div>
    </div>
  );
}


export function checkAnswer(task, userInput = {}) {
  const expected = task?.data?.domainIntervals;
  if (!Array.isArray(expected) || expected.length === 0) return false;

  const raw = getIntervalRaw(userInput, "D");

  if (expected.length === 1) {
    const parsed = parseSingleInterval(raw);
    if (!parsed) return false;

    return intervalsEqualList(
      [
        {
          left: expected[0].left,
          right: expected[0].right,
          leftClosed: !!expected[0].leftClosed,
          rightClosed: !!expected[0].rightClosed,
        },
      ],
      [
        {
          left: parsed.left,
          right: parsed.right,
          leftClosed: !!parsed.leftClosed,
          rightClosed: !!parsed.rightClosed,
        },
      ]
    );
  }

  // expected length >= 2
  const parsedUnion = parseIntervalsUnion(raw);
  if (!parsedUnion) return false;

  return intervalsEqualList(expected, parsedUnion);
}

export function renderAnswer(task) {
  const expected = task?.data?.domainIntervals;
  if (!Array.isArray(expected) || expected.length === 0) return null;

  const toLatex = (I) => {
    const l = I.leftClosed ? "[" : "(";
    const r = I.rightClosed ? "]" : ")";
    return `${l}${I.left},\\,${I.right}${r}`;
  };

  if (expected.length === 1) {
    return <InlineMath math={`x\\in ${toLatex(expected[0])}`} />;
  }

  return (
    <InlineMath
      math={`x\\in ${expected.map(toLatex).join("\\cup")}`}
    />
  );
}

export function renderExplanation(task) {
  const expected = task?.data?.domainIntervals;
  if (!Array.isArray(expected) || expected.length === 0) return null;

  const toText = (I) => {
    const l = I.leftClosed ? "[" : "(";
    const r = I.rightClosed ? "]" : ")";
    return `${l}${I.left}, ${I.right}${r}`;
  };

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Wyjaśnienie.</strong></div>
      <div>
        Dziedzina to wszystkie argumenty (wartości na osi <InlineMath math={'x'} />),
        dla których na wykresie istnieje punkt funkcji.
      </div>

      {expected.length === 1 ? (
        <div style={{ marginTop: 8 }}>
          Wykres istnieje dla argumentów od <InlineMath math={`${expected[0].left}`} /> do{" "}
          <InlineMath math={`${expected[0].right}`} />, z uwzględnieniem kółek na końcach,
          więc dziedzina to <strong>{toText(expected[0])}</strong>.
        </div>
      ) : (
        <div style={{ marginTop: 8 }}>
          Wykres ma <strong>dwie rozłączne części</strong>, więc dziedzina jest sumą przedziałów:
          <div style={{ marginTop: 6 }}>
            {expected.map((I, i) => (
              <div key={i}>
                <InlineMath math={`D_${i + 1} = ${I.leftClosed ? "[" : "("}${I.left},\\,${I.right}${I.rightClosed ? "]" : ")"}`} />
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ marginTop: 10 }}>
        <InlineMath
          math={`x\\in ${expected
            .map(
              (I) =>
                `${I.leftClosed ? "[" : "("}${I.left},\\,${I.right}${
                  I.rightClosed ? "]" : ")"
                }`
            )
            .join("\\cup")}`}
        />
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = getIntervalRaw(userAnswer, "D");
  if (!raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`x\\in ${raw}`} />;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
